import { Col, Row } from "react-bootstrap";
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
} from "react";
import { useGet, usePost } from "@/apiHooks/HttpFunctions";
import { LIST_MODE, LIST_PER_PAGE } from "@/constant/Lists/listMode";
import BasicInput from "@/components/BasicInput";
import BasicModal from "@/Modals/BasicModal";
import ImageInput from "@/components/ImageInput/Index";
import RenderToast from "@/components/RenderToast";
import StatusInput from "@/components/StatusInput";
import getBackgroundInitialValues from "@/data/ContentManagement/Backgrounds/initial-values";
import { backgroundsApi, sportsMasterDataApi } from "@/apis/apis";
import useCloseModalOnNotFound from "@/CustomHooks/useCloseModalOnNotFound";
import useFormApiState from "@/CustomHooks/useFormApiState";
import { useFormik } from "formik";
import useFormikErrorToast from "@/CustomHooks/useFormikErrorToast";
import useHttpErrorToast from "@/CustomHooks/useHttpErrorToast";
import AsyncDropdown from "@/components/AsyncDropdown/AsyncDropdown";
import useModalGetOptions from "@/CustomHooks/Modal/useModalGetOptions";
import BackgroundSchema from "@/schema/ContentManagement/Backgrounds/BackgroundSchema";
import { PAGE, pageBackgroundFormOptions } from "@/constant/Pages/page";

const BackgroundModal = forwardRef((_, ref) => {
  const [modalState, setModalState] = useState({ isOpen: false, id: null });
  const showApi = modalState.id ? `${backgroundsApi}/${modalState.id}` : null;

  const { data: showData, error: showError } = useGet({
    apiURL: showApi,
    enabled: !!modalState.id,
  });

  const handleSuccess = (action) => (data) => {
    closeModal();
    RenderToast({
      type: "success",
      message: `Background ${action} successfully.`,
    });
  };

  const {
    mutate: saveEdit,
    isPending: saveEditIsPending,
    error: saveEditError,
  } = usePost({
    apiURL: `${backgroundsApi}/${modalState.id}`,
    isFormData: true,
    onSuccess: handleSuccess("edited"),
  });
  const {
    mutate: saveCreate,
    isPending: saveCreateIsPending,
    error: saveCreateError,
  } = usePost({
    apiURL: backgroundsApi,
    isFormData: true,
    onSuccess: handleSuccess("added"),
  });
  const { saveIsPending, saveError, resetError } = useFormApiState(
    [saveEditIsPending, saveCreateIsPending],
    [saveEditError, saveCreateError]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: getBackgroundInitialValues(modalState.id ? showData : null),
    validationSchema: BackgroundSchema(!!modalState.id),
    onSubmit: (values) => handleSubmit(values),
  });

  const baseQueryParams = `?mode=${LIST_MODE.SELECT}&perPage=${LIST_PER_PAGE}&filter[keyword]=`;
  const sportListApi = `${sportsMasterDataApi}${baseQueryParams}`;
  const {
    loading: sportLoading,
    defaultOptions: sportDefaultOptions,
    debouncedFetchOptions: sportFetchOptions,
  } = useModalGetOptions({
    modalState,
    listApi: sportListApi,
    formikValue: formik.values.sport_id,
    formikInitialValue: formik.initialValues.sport_id,
    showData: !!modalState?.id && showData?.data?.sport,
  });
  const allSportOption = [
    { label: "SP-All", value: "all" },
    ...sportDefaultOptions,
  ];

  const handleSubmit = (values) => {
    const formData = new FormData();
    if (modalState.id) {
      formData.append("_method", "PUT");
    }
    formData.append("name", values.name);
    formData.append("page", values.page);

    formData.append("sport_id", values.sport_id);

    formData.append("url_redirect", values.url_redirect);
    formData.append("status", values.status);

   
    if (values.image_url && values.image_url instanceof File)
      formData.append("image_url", values.image_url);

    modalState.id ? saveEdit(formData) : saveCreate(formData);
  };

  useImperativeHandle(ref, () => ({
    open: (props) => setModalState({ isOpen: true, id: props?.id }),
    close: closeModal,
  }));

  const closeModal = useCallback(() => {
    formik.resetForm();
    resetError();
    setModalState({ isOpen: false, id: null });
  }, [resetError, formik]);

  useCloseModalOnNotFound(showError, closeModal);
  useFormikErrorToast(formik);
  useHttpErrorToast(saveError);

  return (
    <BasicModal
      header={modalState.id ? "Edit Background" : "Add Background"}
      onHide={closeModal}
      show={modalState.isOpen}
      saveLoading={saveIsPending}
      formik={formik}
    >
      <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            fieldName={"name"}
            saveError={saveError}
            label="Name"
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <AsyncDropdown
            isStatic={true}
            label="Page"
            placeholder="No pages selected. Type to search..."
            value={
              pageBackgroundFormOptions.find(
                (option) => option.value === formik.values.page
              ) || null
            }
            options={pageBackgroundFormOptions}
            onChange={(selected) => {
              if (selected.value === PAGE.USER_PROFILE) {
                formik.setFieldValue("sport_id", "all");
              }
              formik.setFieldValue("page", selected.value);
            }}
            errorText={
              (formik.touched.page && formik.errors.page) ||
              (saveError?.status === 422 &&
                saveError?.response?.data?.errors?.page?.join(" "))
            }
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <AsyncDropdown
            label="Sport"
            placeholder="No sports selected. Type to search..."
            defaultOptions={allSportOption}
            loadOptions={sportFetchOptions}
            isLoading={sportLoading}
            value={
              allSportOption.find(
                (option) => option.value === formik.values.sport_id 
              ) || null
            }
            onChange={(selected) => {
              formik.setFieldValue("sport_id", selected?.value || "all");
            }}
            errorText={
              (formik.touched.sport_id && formik.errors.sport_id) ||
              (saveError?.status === 422 &&
                saveError?.response?.data?.errors?.sport_id?.join(" "))
            }
            isDisabled={formik.values.page === PAGE.USER_PROFILE}
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <StatusInput formik={formik} saveError={saveError} />
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <ImageInput
            formFormik={formik}
            fieldName="image_url"
            label="Image"
            error={saveError}
            existingImageUrl={modalState.id && showData?.data?.image_url}
          />
        </Col>
      </Row>
    </BasicModal>
  );
});

export default BackgroundModal;
